import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  User, MapPin, Briefcase, GraduationCap, CreditCard, Users,
  X, CheckCircle, Clock, Building, FileText, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function CandidateDraftViewerReadOnly({ open, onClose, employeeId }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Data states
  const [employee, setEmployee] = useState(null);
  const [entity, setEntity] = useState(null);
  const [client, setClient] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (open && employeeId) {
      fetchDraftDetails();
    }
  }, [open, employeeId]);

  const fetchDraftDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/candidate-drafts/${employeeId}`);
      const { employee, entity, client, package: pkg, progress } = response.data;
      
      setEmployee(employee);
      setEntity(entity);
      setClient(client);
      setPackageData(pkg);
      setProgress(progress);
    } catch (error) {
      toast.error('Failed to load candidate details');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label, value, icon = null) => (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
        {icon}
        <span className="text-sm text-slate-700">{value || '-'}</span>
      </div>
    </div>
  );

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Eye className="w-5 h-5 text-slate-500" />
                {employee?.name || 'Candidate Details'}
                <Badge variant="outline" className="ml-2">View Only</Badge>
              </DialogTitle>
              <p className="text-sm text-slate-500 mt-1">
                {employee?.email} • {client?.company_name || 'Unknown Client'}
              </p>
            </div>
            {progress && (
              <div className="text-right">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm text-slate-500">Data Entry Progress:</span>
                  <Badge variant={progress.percentage >= 80 ? 'default' : 'secondary'}>
                    {progress.percentage}%
                  </Badge>
                </div>
                <Progress value={progress.percentage} className="w-32 h-2" />
              </div>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-500">Loading candidate details...</div>
        ) : (
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-3">
              <Eye className="w-5 h-5 text-slate-500 mt-0.5" />
              <div className="text-sm text-slate-600">
                <strong>View Only Mode:</strong> You are viewing data entered by the candidate. 
                Contact the verification agency if you need to make any changes.
              </div>
            </div>

            {/* Section Progress */}
            <div className="grid grid-cols-6 gap-2">
              {[
                { key: 'basic', label: 'Basic', icon: User },
                { key: 'address', label: 'Address', icon: MapPin },
                { key: 'employment', label: 'Employment', icon: Briefcase },
                { key: 'education', label: 'Education', icon: GraduationCap },
                { key: 'id_details', label: 'ID Details', icon: CreditCard },
                { key: 'references', label: 'References', icon: Users },
              ].map(({ key, label, icon: Icon }) => (
                <div 
                  key={key}
                  className={`p-2 rounded-lg border text-center ${
                    progress?.sections?.[key] 
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-slate-50 border-slate-200 text-slate-500'
                  }`}
                >
                  <Icon className="w-4 h-4 mx-auto mb-1" />
                  <span className="text-xs font-medium">{label}</span>
                  {progress?.sections?.[key] && (
                    <CheckCircle className="w-3 h-3 mx-auto mt-1 text-emerald-500" />
                  )}
                </div>
              ))}
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-6 w-full">
                <TabsTrigger value="basic">Basic</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
                <TabsTrigger value="employment">Employment</TabsTrigger>
                <TabsTrigger value="education">Education</TabsTrigger>
                <TabsTrigger value="id">ID Details</TabsTrigger>
                <TabsTrigger value="references">References</TabsTrigger>
              </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Basic Information</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    {renderField('Full Name', employee?.name)}
                    {renderField('Email', employee?.email)}
                    {renderField('Gender', employee?.gender)}
                    {renderField('Date of Birth', employee?.dob)}
                    {renderField('Phone', employee?.phone)}
                    {renderField('Alternate Contact', employee?.alternate_contact)}
                    {renderField('Father\'s Name', employee?.father_name)}
                    {renderField('Department', employee?.department)}
                    {renderField('Designation', employee?.designation)}
                    {renderField('Date of Joining', employee?.date_of_joining)}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Address Tab */}
              <TabsContent value="address" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Present Address</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {renderField('Complete Address', employee?.present_address?.complete_address)}
                    <div className="grid grid-cols-3 gap-4">
                      {renderField('City', employee?.present_address?.city)}
                      {renderField('State', employee?.present_address?.state)}
                      {renderField('Pincode', employee?.present_address?.pincode)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Permanent Address</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {renderField('Complete Address', employee?.permanent_address?.complete_address)}
                    <div className="grid grid-cols-3 gap-4">
                      {renderField('City', employee?.permanent_address?.city)}
                      {renderField('State', employee?.permanent_address?.state)}
                      {renderField('Pincode', employee?.permanent_address?.pincode)}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Employment Tab */}
              <TabsContent value="employment" className="space-y-4">
                {employee?.employment_entries?.length > 0 ? (
                  employee.employment_entries.map((entry, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <CardTitle className="text-lg">
                          Employment {idx + 1}
                          {idx === 0 && <Badge className="ml-2">Current/Most Recent</Badge>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4">
                        {renderField('Company Name', entry.company_name)}
                        {renderField('Designation', entry.designation)}
                        {renderField('From Date', entry.employed_from)}
                        {renderField('To Date', entry.employed_to || 'Present')}
                        {renderField('HR Email', entry.hr_email)}
                        {renderField('HR Phone', entry.hr_phone)}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center text-slate-500">
                      {employee?.employment_declaration 
                        ? 'Candidate declared no employment history'
                        : 'No employment data entered yet'}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Education Tab */}
              <TabsContent value="education" className="space-y-4">
                {employee?.education_entries?.length > 0 ? (
                  employee.education_entries.map((entry, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <CardTitle className="text-lg">
                          Education {idx + 1}
                          {idx === 0 && <Badge className="ml-2">Highest Qualification</Badge>}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4">
                        {renderField('Course/Degree', entry.course)}
                        {renderField('University', entry.university_name)}
                        {renderField('College/Institution', entry.college)}
                        {renderField('Year of Passing', entry.year_of_passing)}
                        {renderField('Roll Number', entry.roll_number)}
                        {renderField('Percentage/CGPA', entry.percentage)}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center text-slate-500">
                      {employee?.education_declaration 
                        ? 'Candidate declared no formal education'
                        : 'No education data entered yet'}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* ID Details Tab */}
              <TabsContent value="id" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Identity Documents</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4">
                    {renderField('PAN Number', employee?.pan_number)}
                    {renderField('Aadhar Number', employee?.aadhar_number)}
                    {renderField('Passport Number', employee?.passport_number)}
                    {renderField('Driving License', employee?.driving_license)}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* References Tab */}
              <TabsContent value="references" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Personal References</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {employee?.personal_references?.length > 0 ? (
                      <div className="space-y-3">
                        {employee.personal_references.map((ref, idx) => (
                          <div key={idx} className="grid grid-cols-3 gap-4 p-3 bg-slate-50 rounded-lg">
                            {renderField('Name', ref.name)}
                            {renderField('Phone', ref.phone)}
                            {renderField('Relation', ref.relation)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No personal references entered</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Professional References</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {employee?.professional_references?.length > 0 ? (
                      <div className="space-y-3">
                        {employee.professional_references.map((ref, idx) => (
                          <div key={idx} className="grid grid-cols-3 gap-4 p-3 bg-slate-50 rounded-lg">
                            {renderField('Name', ref.name)}
                            {renderField('Company', ref.company)}
                            {renderField('Phone', ref.phone)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No professional references entered</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Close Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
